






































import { computed, defineComponent, ref } from "@vue/composition-api";
import { UserVerification, UserVerificationTypeEnum } from "@scrile/api-provider/dist/api/UserVerificationProvider";
import useAuthUser from "@/hooks/useAuthUser";
import useFiles from "@/hooks/useFiles";
import useUserVerification from "@/hooks/useUserVerification";
import providers from "@/lib/api";
import { FormValues } from "@/types";
import BaseButton from "@/components/BaseButton";
import BaseForm from "@/components/BaseForm";
import PageTitle from "@/components/PageTitle/PageTitle";
import { personalIdFields } from "@/views/PagePayeeInfo/config";
import AgeVerificationInputFile from "./components/AgeVerificationInputFile.vue";
import PersonalIdExampleConfirm from "./components/PersonalIdExampleConfirm.vue";

export default defineComponent({
  name: "AgeVerification",
  components: {
    PageTitle,
    BaseButton,
    AgeVerificationInputFile,
    BaseForm,
    PersonalIdExampleConfirm,
  },
  setup(_, { emit }) {
    const { authUserStrict, updateUser } = useAuthUser();
    const { userVerificationSearch, userVerificationStart } = useUserVerification();
    const { deleteFile, uploadThenUpdate } = useFiles();
    const userVerificationStatus = ref<UserVerification | null>(null);
    const showExampleModal = ref(false);
    const submitting = ref(false);

    const progress = ref<Record<string, { progress: number; isLoading: boolean }>>({
      id_photo: { progress: 100, isLoading: false },
      photo_with_id: { progress: 100, isLoading: false },
    });

    const fields = computed(() => personalIdFields());

    const values = computed<FormValues>(() => ({
      id_photo: { data: userVerificationStatus.value?.idPhoto || null },
      photo_with_id: { data: userVerificationStatus.value?.photoWithId || null },
    }));

    const getUserVerification = async () => {
      let data = await userVerificationSearch({
        userIds: [authUserStrict.value.id],
        type: UserVerificationTypeEnum.AGE_VERIFICATION,
      });
      userVerificationStatus.value = data.result.find(i => i.userId === authUserStrict.value.id) || null;
    };

    const photoFieldMap: Record<string, keyof UserVerification> = {
      id_photo: "idPhoto",
      photo_with_id: "photoWithId",
    };

    const isValid = computed(() => {
      return fields.value.every(field => {
        if (!field.meta?.required) return true;
        const photoKey = photoFieldMap[field.name];
        return !!userVerificationStatus.value?.[photoKey];
      });
    });

    const upload = async (file: FileList | null, type: string) => {
      if (file) {
        progress.value[type].progress = 0;
        progress.value[type].isLoading = true;

        await uploadThenUpdate({
          fileId: { subjectId: authUserStrict.value.id, type },
          file: file[0],
          onUploadProgress: uploadProgress => {
            const res = ((uploadProgress?.loaded ?? 0) / (uploadProgress?.total ?? 0)) * 100;
            progress.value[type].progress = res === 100 ? 99.9 : res;
          },
          onSuccess: () => {
            progress.value[type].isLoading = false;
            progress.value[type].progress = 100;
          },
        });
      } else {
        await deleteFile({ fileId: { subjectId: authUserStrict.value.id, type: type } });
      }
      await getUserVerification();
    };

    const onSubmit = async () => {
      if (!isValid.value || submitting.value) return;

      try {
        submitting.value = true;
        await userVerificationStart({
          userId: authUserStrict.value.id,
          type: UserVerificationTypeEnum.AGE_VERIFICATION,
        });
        const verificationData = await userVerificationSearch({
          userIds: [authUserStrict.value.id],
          type: UserVerificationTypeEnum.AGE_VERIFICATION,
        });
        const ageVerification = verificationData.result.find(i => i.userId === authUserStrict.value.id) || null;
        const { role, authorities } = await providers.UserProvider.getMyAuthorities();
        updateUser({ ...authUserStrict.value, role, ageVerification }, authorities);
        emit("continue");
      } finally {
        submitting.value = false;
      }
    };

    getUserVerification();

    return {
      showExampleModal,
      values,
      fields,
      progress,
      upload,
      isValid,
      submitting,
      onSubmit,
    };
  },
});
